import { useCallback, useEffect, useMemo } from "react"
import { useController, useFormContext, useWatch } from "react-hook-form"
import { useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import type { SelectProps } from "./Select"
import { Select } from "./Select"

export interface SelectFieldProps extends Omit<SelectProps, "value"> {
	name: string
	shouldSuppressErrors?: boolean
	shouldUnregister?: boolean
}

export const SelectField = (props: SelectFieldProps) => {
	const {
		disabledHint,
		name,
		onChange,
		options,
		required,
		shouldSuppressErrors,
		shouldUnregister = true,
		...rest
	} = props

	const { formatMessage } = useIntl()

	const rules = useMemo(() => {
		return {
			required: {
				message: formatMessage(formMessages.required),
				value: !!required,
			},
		}
	}, [required, formatMessage])

	const { control, trigger } = useFormContext()
	const {
		field: { onChange: fieldOnChange, ref: fieldRef },
		fieldState: { error, isDirty, isTouched },
		formState: { isSubmitted },
	} = useController({
		control,
		name,
		rules,
		shouldUnregister,
	})

	const handleUpdate = useCallback(
		(value: string | null) => {
			fieldOnChange(value)
			onChange?.(value)
		},
		[fieldOnChange, onChange],
	)

	// Getting the value via useWatch instead of the field state because the field state is not updated when the value is set programmatically (e.g. via setValue)
	const fieldValue = useWatch({ name })

	// Fields need to retrigger validation in order to apply changed rules, since the rules are cached inside react-hook-form.
	// See https://github.com/react-hook-form/react-hook-form/issues/1749 and https://github.com/react-hook-form/react-hook-form/issues/8310
	useEffect(() => {
		if (!shouldSuppressErrors && (isDirty || isSubmitted || isTouched)) {
			trigger(name)
		}
		// rules and value need to be part of it because either one can lead to a change of validity
	}, [shouldSuppressErrors, rules, fieldValue, trigger, name, isDirty, isTouched, isSubmitted])

	return (
		<Select
			data-testid={`select-field_${name}`}
			{...rest}
			options={options}
			ref={fieldRef}
			required={required}
			disabledHint={disabledHint}
			name={name}
			onChange={handleUpdate}
			value={fieldValue}
			error={error?.message}
		/>
	)
}
